import { inject, Injectable } from '@angular/core';
import { Company, GetCompanyDetailsOpts, User } from '@interfaces';
import { Actions, ofType } from '@ngrx/effects';

import { Store } from '@ngrx/store';

import { AppState } from '@state';
import { merge, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import * as companyActions from './company.actions';
import * as companySelectors from './company.selectors';
import { CreateManagerOpts, SearchManagersOfCompanyOpts, UpdateManagerOpts } from 'src/app/_interfaces/manager';

@Injectable()
export class CompanyFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);

  public loadedCompanyDetails$ = this.store.select(companySelectors.selectLoadedCompanyDetails);

  public loading$ = this.store.select(companySelectors.selectLoading);
  public errorMessage$ = this.store.select(companySelectors.selectErrorMessage);

  public companyEmployeesProcesses$ = this.store.select(companySelectors.selectEmployeesProcesses);
  public companyEmployeesProcessesPagination$ = this.store.select(companySelectors.selectEmployeesProcessesPagination);

  public saveSuccess$ = this.actions.pipe(
    ofType(companyActions.updateCompanySuccess, companyActions.createCompanySuccess),
    map(
      (result: { updatedCompany?: Company; createdCompany?: Company }) => result.updatedCompany || result.createdCompany
    )
  );

  public updateError$ = this.actions.pipe(ofType(companyActions.updateCompanyError));
  public getCompanyDetailsError$ = this.actions.pipe(ofType(companyActions.getCompanyDetailsError));

  public managers$ = this.store.select(companySelectors.selectManagers);
  public managersPagination$ = this.store.select(companySelectors.selectManagersPagination);
  public loadingManagersList$ = this.store.select(companySelectors.selectManagersListLoading);

  public managerDetails$ = this.store.select(companySelectors.selectLoadedManagerDetails);
  public loadingManagerDetails$ = this.store.select(companySelectors.selectLoadingManagerDetails);
  public addManagedCompanySuccess$ = this.actions.pipe(ofType(companyActions.addManagedCompanySuccess));
  public removeManagedCompanySuccess$ = this.actions.pipe(ofType(companyActions.removeManagedCompanySuccess));

  public loadingCompanyOfSelfRegistered$ = this.store.select(companySelectors.selectLoadingCompanyOfSelfRegistered);
  public companyOfSelfRegistered$ = this.store.select(companySelectors.selectCompanyOfSelfRegistered);
  public allActiveCompanies$ = this.store.select(companySelectors.selectAllActiveCompanies);
  public allInactiveCompanies$ = this.store.select(companySelectors.selectAllInactiveCompanies);

  public getAllActiveCompanies(): void {
    this.allActiveCompanies$.pipe(take(1)).subscribe(companies => {
      if (companies) {
        return;
      }

      this.store.dispatch(companyActions.getAllActiveCompanies());
    });
  }

  public getAllInactiveCompanies(): void {
    this.allInactiveCompanies$.pipe(take(1)).subscribe(companies => {
      if (companies) {
        return;
      }

      this.store.dispatch(companyActions.getAllInactiveCompanies());
    });
  }

  public resetLoadedCompanyDetails(): void {
    this.store.dispatch(companyActions.resetLoadedCompanyDetails());
  }

  public getCompanyDetails(companyId: string, opts: GetCompanyDetailsOpts): void {
    this.store.dispatch(companyActions.getCompanyDetails({ companyId, opts }));
  }

  public getCompanyDetails$(companyId: string, opts: GetCompanyDetailsOpts): Observable<Company | null> {
    return this.loadedCompanyDetails$.pipe(
      take(1),
      switchMap(company => {
        if (company?.id === companyId) {
          return of(company);
        }

        this.getCompanyDetails(companyId, opts);

        return merge(
          this.loadedCompanyDetails$.pipe(filter(loadedCompany => loadedCompany?.id === companyId)),
          this.getCompanyDetailsError$.pipe(map((): null => null))
        ).pipe(take(1));
      })
    );
  }

  public createCompany(company: Company): void {
    this.store.dispatch(companyActions.createCompany({ company }));
  }

  public updateCompany(companyId: string, company: Company): void {
    this.store.dispatch(companyActions.updateCompany({ companyId, company }));
  }

  public setIsActiveCompany(companyId: string, isActive: boolean): void {
    this.store.dispatch(companyActions.setIsActiveCompany({ companyId, isActive }));
  }

  public getManagerDetails$(managerId: string): Observable<Partial<User> | null> {
    return this.store.select(companySelectors.selectLoadedManagerDetails).pipe(
      tap(manager => {
        if (manager?.id !== managerId) {
          this.getManagerDetails(managerId);
        }
      }),
      filter(manager => {
        if (manager?.id !== managerId) {
          return false;
        }
        return true;
      })
    );
  }

  public searchManagersOfCompany(payload: SearchManagersOfCompanyOpts): void {
    this.store.dispatch(companyActions.searchManagersOfCompany({ payload }));
  }

  public createManager(payload: CreateManagerOpts): void {
    this.store.dispatch(companyActions.createManager({ payload }));
  }

  public getManagerDetails(managerId: string): void {
    this.store.dispatch(companyActions.getManagerDetails({ managerId }));
  }

  public resendManagerActivation(email: string, userId: string): void {
    this.store.dispatch(companyActions.resendManagerActivation({ email, userId }));
  }

  public updateManager(payload: UpdateManagerOpts, userId: string): void {
    this.store.dispatch(companyActions.updateManager({ payload, userId }));
  }
  public softRemoveManager(managerId: string): void {
    this.store.dispatch(companyActions.softRemoveManager({ managerId }));
  }
  public restoreManager(managerId: string): void {
    this.store.dispatch(companyActions.restoreManager({ managerId }));
  }

  public addManagedCompany(opts: { managerId: string; companyId: string }) {
    this.store.dispatch(companyActions.addManagedCompany(opts));
  }
  public removeManagedCompany(opts: { mocId: string; managerId: string }) {
    this.store.dispatch(companyActions.removeManagedCompany(opts));
  }

  public getCompanyOfSelfRegistered(): void {
    this.store.dispatch(companyActions.getCompanyOfSelfRegistered());
  }
}
